import React from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import IconButton from '@mui/material/IconButton';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  ClaimLogEntryViewDTO,
} from 'dtos';
import ToolTip from 'core/components/Tooltip';
import CopyWrapper from 'core/components/CopyWrapper';
import {
  formatDateTime,
} from 'utils/date';
import {
  getActivityDetail,
} from 'pages/Dashboard/pages/Billing/ClaimForm/ClaimActivity/helpers';
import ActivityStatusTag from 'pages/Dashboard/pages/Billing/ClaimForm/ClaimActivity/ActivityStatusTag';

type ActivityEntryDetailsProps = {
  entry: ClaimLogEntryViewDTO;
};

function ActivityEntryDetails({ entry }: ActivityEntryDetailsProps) {
  const detail = getActivityDetail(entry);
  const messages = (entry.messages ?? []).filter(notEmptyString);

  return (
    <div className="flex flex-col gap-1 text-gray-700">
      {detail.map((item) => (
        <div
          key={`${item.label ?? ''}-${item.value}`}
          className="whitespace-pre-line flex items-center font-semibold"
        >
          {notEmptyString(item.label) && (
            <span className="text-gray-900 font-semibold">{item.label}:&nbsp;</span>
          )}
          {notEmptyString(item.copyText) ? (
            <CopyWrapper
              text={item.copyText}
              tooltip="Copy"
              classes={{
                icon: '!w-3 !h-3',
                root: '!p-0.5',
              }}
            >
              <span>{item.value}</span>
            </CopyWrapper>
          ) : (
            item.value
          )}
        </div>
      ))}
      <span>
        {[
          formatDateTime(entry.time ?? '', false),
          entry.triggerActor,
          entry.triggerAction,
        ].filter(notEmptyString).join(' · ')}
      </span>
      {messages.length > 0 && (
        <ul className="list-disc pl-4 text-[13px] font-normal space-y-2 pt-1">
          {messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

type ActivityEntryTitleProps = {
  entry: ClaimLogEntryViewDTO;
};

export function ActivityEntryTitle({ entry }: ActivityEntryTitleProps) {
  return (
    <span className="font-semibold text-sm text-gray-900 -mt-0.5">
      {entry.activity ?? 'Activity'}
      {!entry.isSuccess && ' (Failed)'}
    </span>
  );
}

type Props = {
  entry: ClaimLogEntryViewDTO;
  onViewActivity?: () => void;
  className?: string;
  hideTitle?: boolean;
};

export default function ActivityEntry({
  entry,
  onViewActivity,
  className,
  hideTitle,
}: Props) {
  const hasViewActivity = !isNil(onViewActivity);

  return (
    <div
      className={clsx(
        'flex flex-col gap-1 text-xs text-gray-500 font-medium',
        className,
      )}
    >
      <div className="flex justify-between items-center gap-2 min-h-5">
        {!hideTitle && (
          <ActivityEntryTitle entry={entry} />
        )}
        {(notEmptyString(entry.afterStatus) || !isNil(entry.codes) || hasViewActivity) && (
          <div className="flex items-center gap-1 shrink-0 min-h-5">
            <ActivityStatusTag
              afterStatus={entry.afterStatus}
              codes={entry.codes}
            />
            {hasViewActivity && (
              <ToolTip title="View Activity">
                <IconButton
                  size="small"
                  onClick={onViewActivity}
                >
                  <HistoryRoundedIcon className="!w-4 !h-4" />
                </IconButton>
              </ToolTip>
            )}
          </div>
        )}
      </div>
      <ActivityEntryDetails entry={entry} />
    </div>
  );
}
